import { ChevronDown } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { cn } from '@/lib/cn';
import { searchKey } from '@/lib/search-key';

export interface SearchableSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SearchableSelectGroup {
  readonly label: string;
  readonly options: readonly SearchableSelectOption[];
}

/**
 * O select que se pesquisa **dentro dele mesmo** (pedido do Gilvan, 30/10/2026).
 *
 * O pavimento tem dezenas de serviços, e rolar o select nativo no tablet para achar um era
 * lento. Uma caixa de busca acima dele resolveria, mas ocupa espaço e muda o desenho do
 * formulário; o pedido foi pesquisar no próprio campo. O `<select>` do navegador não aceita
 * digitar, então este é um campo com a cara dele que, ao tocar, abre a lista inteira — os
 * grupos e as opções, como o select — e, ao digitar, a estreita pelo nome da opção ou do
 * grupo, sem ligar para acento.
 *
 * Fechado, mostra a opção escolhida; aberto, o que se digita. As setas andam, Enter escolhe
 * (digitou, o primeiro achado já vem destacado) e Esc fecha: é o `role="combobox"` completo,
 * como o da busca do compras. Enter nunca envia o formulário em volta — com o select, Enter
 * também não enviava.
 *
 * `fixedOptions` vão no fim e a pesquisa não as esconde: são a saída de quem não achou o que
 * procurava (a atividade avulsa, na Conferência).
 */
export function SearchableSelect({
  id,
  value,
  groups,
  fixedOptions = [],
  placeholder = 'Selecione…',
  searchPlaceholder = 'Pesquisar…',
  emptyText,
  disabled = false,
  className,
  onChange,
}: {
  id: string;
  value: string;
  groups: readonly SearchableSelectGroup[];
  fixedOptions?: readonly SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** A frase de quando a pesquisa não acha nada nos grupos. */
  emptyText: (term: string) => string;
  disabled?: boolean;
  className?: string;
  onChange: (value: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [destacado, setDestacado] = useState<number | undefined>(undefined);

  const escolhida = [...groups.flatMap((grupo) => grupo.options), ...fixedOptions].find(
    (opcao) => opcao.value === value,
  );

  const termo = searchKey(busca);
  const gruposVisiveis = groups
    .map((grupo) => ({
      ...grupo,
      options:
        termo === '' || searchKey(grupo.label).includes(termo)
          ? grupo.options
          : grupo.options.filter((opcao) => searchKey(opcao.label).includes(termo)),
    }))
    .filter((grupo) => grupo.options.length > 0);
  const visiveis = [...gruposVisiveis.flatMap((grupo) => grupo.options), ...fixedOptions];
  const indiceDe = new Map(visiveis.map((opcao, indice) => [opcao.value, indice]));

  const fechar = (): void => {
    setAberto(false);
    setBusca('');
    setDestacado(undefined);
  };

  const escolher = (opcao: SearchableSelectOption): void => {
    fechar();
    if (opcao.value !== value) onChange(opcao.value);
  };

  const aoTeclar = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      if (aberto) event.preventDefault();
      fechar();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setAberto(true);
      if (visiveis.length === 0) return;
      const passo = event.key === 'ArrowDown' ? 1 : -1;
      setDestacado((atual) => {
        if (atual === undefined) return passo === 1 ? 0 : visiveis.length - 1;
        return (atual + passo + visiveis.length) % visiveis.length;
      });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const opcao = destacado === undefined ? undefined : visiveis[destacado];
      if (aberto && opcao !== undefined) escolher(opcao);
      else setAberto(true);
    }
  };

  const idDaOpcao = (indice: number): string => `${id}-op-${String(indice)}`;

  const opcaoNaLista = (opcao: SearchableSelectOption) => {
    const indice = indiceDe.get(opcao.value) ?? -1;
    return (
      <li
        key={opcao.value}
        id={idDaOpcao(indice)}
        role="option"
        aria-selected={opcao.value === value}
        // `onMouseDown` e não `onClick`: o clique tira o foco do campo antes de disparar, e
        // fechar a lista no `blur` engoliria a escolha.
        onMouseDown={(event) => {
          event.preventDefault();
          escolher(opcao);
        }}
        className={cn(
          'cursor-pointer rounded-md px-2 py-2 text-sm md:py-1.5',
          indice === destacado ? 'bg-(--color-surface-muted)' : 'hover:bg-(--color-surface-muted)',
          opcao.value === value && 'font-medium',
        )}
      >
        {opcao.label}
      </li>
    );
  };

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={aberto}
        aria-controls={`${id}-lista`}
        aria-autocomplete="list"
        {...(aberto && destacado !== undefined
          ? { 'aria-activedescendant': idDaOpcao(destacado) }
          : {})}
        disabled={disabled}
        // Fechado, o "Selecione…" tem a cor do texto, como no select ao lado; aberto, o
        // convite à pesquisa fica apagado, como todo placeholder.
        className={cn('w-full pr-8', !aberto && 'placeholder:text-inherit', className)}
        value={aberto ? busca : (escolhida?.label ?? '')}
        placeholder={aberto ? (escolhida?.label ?? searchPlaceholder) : placeholder}
        onFocus={() => setAberto(true)}
        onClick={() => setAberto(true)}
        onBlur={fechar}
        onChange={(event) => {
          setBusca(event.target.value);
          setAberto(true);
          setDestacado(event.target.value.trim() === '' ? undefined : 0);
        }}
        onKeyDown={aoTeclar}
      />
      <ChevronDown
        className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-(--color-content-muted)"
        aria-hidden="true"
      />

      {aberto && (
        <ul
          id={`${id}-lista`}
          role="listbox"
          className="absolute z-30 mt-1 flex max-h-72 w-full flex-col overflow-y-auto rounded-md border border-(--color-border-subtle) bg-(--color-surface-raised) p-1 shadow-lg"
        >
          {gruposVisiveis.map((grupo, indiceDoGrupo) => {
            const idDoGrupo = `${id}-grupo-${String(indiceDoGrupo)}`;
            return (
              <li key={grupo.label} role="presentation">
                <div
                  id={idDoGrupo}
                  className="px-2 pt-2 pb-1 text-xs font-semibold text-(--color-content-muted)"
                >
                  {grupo.label}
                </div>
                <ul role="group" aria-labelledby={idDoGrupo}>
                  {grupo.options.map(opcaoNaLista)}
                </ul>
              </li>
            );
          })}
          {termo !== '' && gruposVisiveis.length === 0 && (
            <li role="presentation" className="px-2 py-2 text-xs text-(--color-content-muted)">
              {emptyText(busca.trim())}
            </li>
          )}
          {fixedOptions.map(opcaoNaLista)}
        </ul>
      )}
    </div>
  );
}
